import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { Ref } from 'vue';
import type { IBetBuilderRecommendation } from '@/interface/IBetBuilderRecommendation';
import type { IBetBuilderMarket, IBetBuilderSelection } from '@/interface/betBuilder';
import { BetBuilderSelectionStatus } from '@/interface/enum';
import { useBetBuilderMatch } from '@/libraries/oddsApi/composables/useBetBuilderMatch';
import useBetBuilderMatchExists from '@/libraries/oddsApi/composables/useBetBuilderMatchExists';
import { useRecommendationEventQuery } from '@/libraries/oddsApi/composables/useRecommendationEventQuery';
import { useBetBuilderRecommendationsByMatch } from '@/libraries/recommendation/useBetBuilderRecommendationsByMatch';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

export interface IPreBuiltSelectionWithMarket {
    market: IBetBuilderMarket;
    selection: IBetBuilderSelection;
}

function findMarketAndSelection(markets: IBetBuilderMarket[], selectionId: number) {
    const market = markets.find(m => m.selections.some(s => s.id === selectionId));
    if (!market) return null;
    const selection = market.selections.find(s => s.id === selectionId);
    return selection ? { market, selection } : null;
}

export function useMatchPreBuiltRecommendation(
    eventId: number,
    isLive: Ref<boolean>,
    isBetBuilderQueryEnable: Ref<boolean>,
) {
    const { isMoreMarketPrebuiltEnabled, moreMarketPrebuiltDisplayRule } = storeToRefs(useFeatureToggleStore());
    const { isBetBuilderExists } = useBetBuilderMatchExists(eventId, isBetBuilderQueryEnable);
    const isPreBuiltQueryEnabled = computed(() => isMoreMarketPrebuiltEnabled.value && isBetBuilderExists.value);

    const { betBuilderRecommendations } = useBetBuilderRecommendationsByMatch(
        eventId,
        isLive,
        moreMarketPrebuiltDisplayRule,
        isPreBuiltQueryEnabled,
    );

    const recommendation = computed<IBetBuilderRecommendation | null>(() => betBuilderRecommendations.value[0] ?? null);
    const recommendationEventId = computed(() => recommendation.value?.eventId ?? 0);
    const isPreBuiltValidationEnabled = computed(() => isPreBuiltQueryEnabled.value && recommendation.value !== null);

    const { event, loaded: eventLoaded } = useRecommendationEventQuery(
        recommendationEventId,
        false,
        isPreBuiltValidationEnabled,
    );
    const { betBuilderMatch, loaded: betBuilderMatchLoaded } = useBetBuilderMatch(
        recommendationEventId,
        isPreBuiltValidationEnabled,
    );

    const selections = computed(() => {
        const ids = recommendation.value?.selectionIds ?? [];
        const markets = betBuilderMatch.value?.markets ?? [];
        return ids
            .map(selectionId => findMarketAndSelection(markets, selectionId))
            .filter((s): s is IPreBuiltSelectionWithMarket => s !== null);
    });

    const hasPreBuilt = computed(() => {
        const rec = recommendation.value;
        const selectionIds = rec?.selectionIds;
        if (!isPreBuiltValidationEnabled.value || !selectionIds) return false;
        return eventLoaded.value
            && betBuilderMatchLoaded.value
            && event.value !== null
            && betBuilderMatch.value !== null
            && selections.value.length === selectionIds.length
            && selections.value.every(s => s.selection.status !== BetBuilderSelectionStatus.Closed);
    });

    return {
        hasPreBuilt,
        recommendation,
        event,
        selections,
    };
}
